import { hash } from 'ohash';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

/**
* @vue/shared v3.4.38
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/
/*! #__NO_SIDE_EFFECTS__ */
// @__NO_SIDE_EFFECTS__
function makeMap(str, expectsLowerCase) {
  const set = new Set(str.split(","));
  return expectsLowerCase ? (val) => set.has(val.toLowerCase()) : (val) => set.has(val);
}

!!(process.env.NODE_ENV !== "production") ? Object.freeze({}) : {};
!!(process.env.NODE_ENV !== "production") ? Object.freeze([]) : [];
const extend = Object.assign;
const hasOwnProperty$1 = Object.prototype.hasOwnProperty;
const hasOwn = (val, key) => hasOwnProperty$1.call(val, key);
const isArray = Array.isArray;
const isMap = (val) => toTypeString(val) === "[object Map]";
const isFunction = (val) => typeof val === "function";
const isString = (val) => typeof val === "string";
const isSymbol = (val) => typeof val === "symbol";
const isObject = (val) => val !== null && typeof val === "object";
const objectToString = Object.prototype.toString;
const toTypeString = (value) => objectToString.call(value);
const toRawType = (value) => {
  return toTypeString(value).slice(8, -1);
};
const isIntegerKey = (key) => isString(key) && key !== "NaN" && key[0] !== "-" && "" + parseInt(key, 10) === key;
const cacheStringFunction = (fn) => {
  const cache = /* @__PURE__ */ Object.create(null);
  return (str) => {
    const hit = cache[str];
    return hit || (cache[str] = fn(str));
  };
};
const capitalize = cacheStringFunction((str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
});
const hasChanged = (value, oldValue) => !Object.is(value, oldValue);

/**
* @vue/reactivity v3.4.38
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/

function warn(msg, ...args) {
  console.warn(`[Vue warn] ${msg}`, ...args);
}

let activeEffectScope;
function recordEffectScope(effect, scope = activeEffectScope) {
  if (scope && scope.active) {
    scope.effects.push(effect);
  }
}

let activeEffect;
class ReactiveEffect {
  constructor(fn, trigger, scheduler, scope) {
    this.fn = fn;
    this.trigger = trigger;
    this.scheduler = scheduler;
    this.active = true;
    this.deps = [];
    /**
     * @internal
     */
    this._dirtyLevel = 4;
    /**
     * @internal
     */
    this._trackId = 0;
    /**
     * @internal
     */
    this._runnings = 0;
    /**
     * @internal
     */
    this._shouldSchedule = false;
    /**
     * @internal
     */
    this._depsLength = 0;
    recordEffectScope(this, scope);
  }
  get dirty() {
    if (this._dirtyLevel === 2 || this._dirtyLevel === 3) {
      this._dirtyLevel = 1;
      pauseTracking();
      for (let i = 0; i < this._depsLength; i++) {
        const dep = this.deps[i];
        if (dep.computed) {
          triggerComputed(dep.computed);
          if (this._dirtyLevel >= 4) {
            break;
          }
        }
      }
      if (this._dirtyLevel === 1) {
        this._dirtyLevel = 0;
      }
      resetTracking();
    }
    return this._dirtyLevel >= 4;
  }
  set dirty(v) {
    this._dirtyLevel = v ? 4 : 0;
  }
  run() {
    this._dirtyLevel = 0;
    if (!this.active) {
      return this.fn();
    }
    let lastShouldTrack = shouldTrack;
    let lastEffect = activeEffect;
    try {
      shouldTrack = true;
      activeEffect = this;
      this._runnings++;
      preCleanupEffect(this);
      return this.fn();
    } finally {
      postCleanupEffect(this);
      this._runnings--;
      activeEffect = lastEffect;
      shouldTrack = lastShouldTrack;
    }
  }
  stop() {
    if (this.active) {
      preCleanupEffect(this);
      postCleanupEffect(this);
      this.onStop && this.onStop();
      this.active = false;
    }
  }
}
function triggerComputed(computed) {
  return computed.value;
}
function preCleanupEffect(effect2) {
  effect2._trackId++;
  effect2._depsLength = 0;
}
function postCleanupEffect(effect2) {
  if (effect2.deps.length > effect2._depsLength) {
    for (let i = effect2._depsLength; i < effect2.deps.length; i++) {
      cleanupDepEffect(effect2.deps[i], effect2);
    }
    effect2.deps.length = effect2._depsLength;
  }
}
function cleanupDepEffect(dep, effect2) {
  const trackId = dep.get(effect2);
  if (trackId !== void 0 && effect2._trackId !== trackId) {
    dep.delete(effect2);
    if (dep.size === 0) {
      dep.cleanup();
    }
  }
}
let shouldTrack = true;
let pauseScheduleStack = 0;
const trackStack = [];
function pauseTracking() {
  trackStack.push(shouldTrack);
  shouldTrack = false;
}
function resetTracking() {
  const last = trackStack.pop();
  shouldTrack = last === void 0 ? true : last;
}
function pauseScheduling() {
  pauseScheduleStack++;
}
function resetScheduling() {
  pauseScheduleStack--;
  while (!pauseScheduleStack && queueEffectSchedulers.length) {
    queueEffectSchedulers.shift()();
  }
}
function trackEffect(effect2, dep, debuggerEventExtraInfo) {
  var _a;
  if (dep.get(effect2) !== effect2._trackId) {
    dep.set(effect2, effect2._trackId);
    const oldDep = effect2.deps[effect2._depsLength];
    if (oldDep !== dep) {
      if (oldDep) {
        cleanupDepEffect(oldDep, effect2);
      }
      effect2.deps[effect2._depsLength++] = dep;
    } else {
      effect2._depsLength++;
    }
    if (!!(process.env.NODE_ENV !== "production")) {
      (_a = effect2.onTrack) == null ? void 0 : _a.call(effect2, extend({ effect: effect2 }, debuggerEventExtraInfo));
    }
  }
}
const queueEffectSchedulers = [];
function triggerEffects(dep, dirtyLevel, debuggerEventExtraInfo) {
  var _a;
  pauseScheduling();
  for (const effect2 of dep.keys()) {
    let tracking;
    if (effect2._dirtyLevel < dirtyLevel && (tracking != null ? tracking : tracking = dep.get(effect2) === effect2._trackId)) {
      effect2._shouldSchedule || (effect2._shouldSchedule = effect2._dirtyLevel === 0);
      effect2._dirtyLevel = dirtyLevel;
    }
    if (effect2._shouldSchedule && (tracking != null ? tracking : tracking = dep.get(effect2) === effect2._trackId)) {
      if (!!(process.env.NODE_ENV !== "production")) {
        (_a = effect2.onTrigger) == null ? void 0 : _a.call(effect2, extend({ effect: effect2 }, debuggerEventExtraInfo));
      }
      effect2.trigger();
      if ((!effect2._runnings || effect2.allowRecurse) && effect2._dirtyLevel !== 2) {
        effect2._shouldSchedule = false;
        if (effect2.scheduler) {
          queueEffectSchedulers.push(effect2.scheduler);
        }
      }
    }
  }
  resetScheduling();
}

const createDep = (cleanup, computed) => {
  const dep = /* @__PURE__ */ new Map();
  dep.cleanup = cleanup;
  dep.computed = computed;
  return dep;
};

const targetMap = /* @__PURE__ */ new WeakMap();
const ITERATE_KEY = Symbol(!!(process.env.NODE_ENV !== "production") ? "iterate" : "");
const MAP_KEY_ITERATE_KEY = Symbol(!!(process.env.NODE_ENV !== "production") ? "Map key iterate" : "");
function track(target, type, key) {
  if (shouldTrack && activeEffect) {
    let depsMap = targetMap.get(target);
    if (!depsMap) {
      targetMap.set(target, depsMap = /* @__PURE__ */ new Map());
    }
    let dep = depsMap.get(key);
    if (!dep) {
      depsMap.set(key, dep = createDep(() => depsMap.delete(key)));
    }
    trackEffect(
      activeEffect,
      dep,
      !!(process.env.NODE_ENV !== "production") ? {
        target,
        type,
        key
      } : void 0
    );
  }
}
function trigger(target, type, key, newValue, oldValue, oldTarget) {
  const depsMap = targetMap.get(target);
  if (!depsMap) {
    return;
  }
  let deps = [];
  if (type === "clear") {
    deps = [...depsMap.values()];
  } else if (key === "length" && isArray(target)) {
    const newLength = Number(newValue);
    depsMap.forEach((dep, key2) => {
      if (key2 === "length" || !isSymbol(key2) && key2 >= newLength) {
        deps.push(dep);
      }
    });
  } else {
    if (key !== void 0) {
      deps.push(depsMap.get(key));
    }
    switch (type) {
      case "add":
        if (!isArray(target)) {
          deps.push(depsMap.get(ITERATE_KEY));
          if (isMap(target)) {
            deps.push(depsMap.get(MAP_KEY_ITERATE_KEY));
          }
        } else if (isIntegerKey(key)) {
          deps.push(depsMap.get("length"));
        }
        break;
      case "delete":
        if (!isArray(target)) {
          deps.push(depsMap.get(ITERATE_KEY));
          if (isMap(target)) {
            deps.push(depsMap.get(MAP_KEY_ITERATE_KEY));
          }
        }
        break;
      case "set":
        if (isMap(target)) {
          deps.push(depsMap.get(ITERATE_KEY));
        }
        break;
    }
  }
  pauseScheduling();
  for (const dep of deps) {
    if (dep) {
      triggerEffects(
        dep,
        4,
        !!(process.env.NODE_ENV !== "production") ? {
          target,
          type,
          key,
          newValue,
          oldValue,
          oldTarget
        } : void 0
      );
    }
  }
  resetScheduling();
}
function getDepFromReactive(object, key) {
  const depsMap = targetMap.get(object);
  return depsMap && depsMap.get(key);
}

const isNonTrackableKeys = /* @__PURE__ */ makeMap(`__proto__,__v_isRef,__isVue`);
const builtInSymbols = new Set(
  /* @__PURE__ */ Object.getOwnPropertyNames(Symbol).filter((key) => key !== "arguments" && key !== "caller").map((key) => Symbol[key]).filter(isSymbol)
);
const arrayInstrumentations = /* @__PURE__ */ createArrayInstrumentations();
function createArrayInstrumentations() {
  const instrumentations = {};
  ["includes", "indexOf", "lastIndexOf"].forEach((key) => {
    instrumentations[key] = function(...args) {
      const arr = toRaw(this);
      for (let i = 0, l = this.length; i < l; i++) {
        track(arr, "get", i + "");
      }
      const res = arr[key](...args);
      if (res === -1 || res === false) {
        return arr[key](...args.map(toRaw));
      } else {
        return res;
      }
    };
  });
  ["push", "pop", "shift", "unshift", "splice"].forEach((key) => {
    instrumentations[key] = function(...args) {
      pauseTracking();
      pauseScheduling();
      const res = toRaw(this)[key].apply(this, args);
      resetScheduling();
      resetTracking();
      return res;
    };
  });
  return instrumentations;
}
function hasOwnProperty(key) {
  if (!isSymbol(key)) key = String(key);
  const obj = toRaw(this);
  track(obj, "has", key);
  return obj.hasOwnProperty(key);
}
class BaseReactiveHandler {
  constructor(_isReadonly = false, _isShallow = false) {
    this._isReadonly = _isReadonly;
    this._isShallow = _isShallow;
  }
  get(target, key, receiver) {
    const isReadonly2 = this._isReadonly, isShallow2 = this._isShallow;
    if (key === "__v_isReactive") {
      return !isReadonly2;
    } else if (key === "__v_isReadonly") {
      return isReadonly2;
    } else if (key === "__v_isShallow") {
      return isShallow2;
    } else if (key === "__v_raw") {
      if (receiver === (isReadonly2 ? isShallow2 ? shallowReadonlyMap : readonlyMap : isShallow2 ? shallowReactiveMap : reactiveMap).get(target) || // receiver is not the reactive proxy, but has the same prototype
      // this means the receiver is a user proxy of the reactive proxy
      Object.getPrototypeOf(target) === Object.getPrototypeOf(receiver)) {
        return target;
      }
      return;
    }
    const targetIsArray = isArray(target);
    if (!isReadonly2) {
      if (targetIsArray && hasOwn(arrayInstrumentations, key)) {
        return Reflect.get(arrayInstrumentations, key, receiver);
      }
      if (key === "hasOwnProperty") {
        return hasOwnProperty;
      }
    }
    const res = Reflect.get(target, key, receiver);
    if (isSymbol(key) ? builtInSymbols.has(key) : isNonTrackableKeys(key)) {
      return res;
    }
    if (!isReadonly2) {
      track(target, "get", key);
    }
    if (isShallow2) {
      return res;
    }
    if (isRef(res)) {
      return targetIsArray && isIntegerKey(key) ? res : res.value;
    }
    if (isObject(res)) {
      return isReadonly2 ? readonly(res) : reactive(res);
    }
    return res;
  }
}
class MutableReactiveHandler extends BaseReactiveHandler {
  constructor(isShallow2 = false) {
    super(false, isShallow2);
  }
  set(target, key, value, receiver) {
    let oldValue = target[key];
    if (!this._isShallow) {
      const isOldValueReadonly = isReadonly(oldValue);
      if (!isShallow(value) && !isReadonly(value)) {
        oldValue = toRaw(oldValue);
        value = toRaw(value);
      }
      if (!isArray(target) && isRef(oldValue) && !isRef(value)) {
        if (isOldValueReadonly) {
          return false;
        } else {
          oldValue.value = value;
          return true;
        }
      }
    }
    const hadKey = isArray(target) && isIntegerKey(key) ? Number(key) < target.length : hasOwn(target, key);
    const result = Reflect.set(target, key, value, receiver);
    if (target === toRaw(receiver)) {
      if (!hadKey) {
        trigger(target, "add", key, value);
      } else if (hasChanged(value, oldValue)) {
        trigger(target, "set", key, value, oldValue);
      }
    }
    return result;
  }
  deleteProperty(target, key) {
    const hadKey = hasOwn(target, key);
    const oldValue = target[key];
    const result = Reflect.deleteProperty(target, key);
    if (result && hadKey) {
      trigger(target, "delete", key, void 0, oldValue);
    }
    return result;
  }
  has(target, key) {
    const result = Reflect.has(target, key);
    if (!isSymbol(key) || !builtInSymbols.has(key)) {
      track(target, "has", key);
    }
    return result;
  }
  ownKeys(target) {
    track(
      target,
      "iterate",
      isArray(target) ? "length" : ITERATE_KEY
    );
    return Reflect.ownKeys(target);
  }
}
class ReadonlyReactiveHandler extends BaseReactiveHandler {
  constructor(isShallow2 = false) {
    super(true, isShallow2);
  }
  set(target, key) {
    if (!!(process.env.NODE_ENV !== "production")) {
      warn(
        `Set operation on key "${String(key)}" failed: target is readonly.`,
        target
      );
    }
    return true;
  }
  deleteProperty(target, key) {
    if (!!(process.env.NODE_ENV !== "production")) {
      warn(
        `Delete operation on key "${String(key)}" failed: target is readonly.`,
        target
      );
    }
    return true;
  }
}
const mutableHandlers = /* @__PURE__ */ new MutableReactiveHandler();
const readonlyHandlers = /* @__PURE__ */ new ReadonlyReactiveHandler();

const toShallow = (value) => value;
const getProto = (v) => Reflect.getPrototypeOf(v);
function get(target, key, isReadonly2 = false, isShallow2 = false) {
  target = target["__v_raw"];
  const rawTarget = toRaw(target);
  const rawKey = toRaw(key);
  if (!isReadonly2) {
    if (hasChanged(key, rawKey)) {
      track(rawTarget, "get", key);
    }
    track(rawTarget, "get", rawKey);
  }
  const { has: has2 } = getProto(rawTarget);
  const wrap = isShallow2 ? toShallow : isReadonly2 ? toReadonly : toReactive;
  if (has2.call(rawTarget, key)) {
    return wrap(target.get(key));
  } else if (has2.call(rawTarget, rawKey)) {
    return wrap(target.get(rawKey));
  } else if (target !== rawTarget) {
    target.get(key);
  }
}
function has(key, isReadonly2 = false) {
  const target = this["__v_raw"];
  const rawTarget = toRaw(target);
  const rawKey = toRaw(key);
  if (!isReadonly2) {
    if (hasChanged(key, rawKey)) {
      track(rawTarget, "has", key);
    }
    track(rawTarget, "has", rawKey);
  }
  return key === rawKey ? target.has(key) : target.has(key) || target.has(rawKey);
}
function size(target, isReadonly2 = false) {
  target = target["__v_raw"];
  !isReadonly2 && track(toRaw(target), "iterate", ITERATE_KEY);
  return Reflect.get(target, "size", target);
}
function add(value, _isShallow = false) {
  if (!_isShallow && !isShallow(value) && !isReadonly(value)) {
    value = toRaw(value);
  }
  const target = toRaw(this);
  const proto = getProto(target);
  const hadKey = proto.has.call(target, value);
  if (!hadKey) {
    target.add(value);
    trigger(target, "add", value, value);
  }
  return this;
}
function set(key, value, _isShallow = false) {
  if (!_isShallow && !isShallow(value) && !isReadonly(value)) {
    value = toRaw(value);
  }
  const target = toRaw(this);
  const { has: has2, get: get2 } = getProto(target);
  let hadKey = has2.call(target, key);
  if (!hadKey) {
    key = toRaw(key);
    hadKey = has2.call(target, key);
  } else if (!!(process.env.NODE_ENV !== "production")) {
    checkIdentityKeys(target, has2, key);
  }
  const oldValue = get2.call(target, key);
  target.set(key, value);
  if (!hadKey) {
    trigger(target, "add", key, value);
  } else if (hasChanged(value, oldValue)) {
    trigger(target, "set", key, value, oldValue);
  }
  return this;
}
function deleteEntry(key) {
  const target = toRaw(this);
  const { has: has2, get: get2 } = getProto(target);
  let hadKey = has2.call(target, key);
  if (!hadKey) {
    key = toRaw(key);
    hadKey = has2.call(target, key);
  } else if (!!(process.env.NODE_ENV !== "production")) {
    checkIdentityKeys(target, has2, key);
  }
  const oldValue = get2 ? get2.call(target, key) : void 0;
  const result = target.delete(key);
  if (hadKey) {
    trigger(target, "delete", key, void 0, oldValue);
  }
  return result;
}
function clear() {
  const target = toRaw(this);
  const hadItems = target.size !== 0;
  const oldTarget = !!(process.env.NODE_ENV !== "production") ? isMap(target) ? new Map(target) : new Set(target) : void 0;
  const result = target.clear();
  if (hadItems) {
    trigger(target, "clear", void 0, void 0, oldTarget);
  }
  return result;
}
function createForEach(isReadonly2, isShallow2) {
  return function forEach(callback, thisArg) {
    const observed = this;
    const target = observed["__v_raw"];
    const rawTarget = toRaw(target);
    const wrap = isShallow2 ? toShallow : isReadonly2 ? toReadonly : toReactive;
    !isReadonly2 && track(rawTarget, "iterate", ITERATE_KEY);
    return target.forEach((value, key) => {
      return callback.call(thisArg, wrap(value), wrap(key), observed);
    });
  };
}
function createIterableMethod(method, isReadonly2, isShallow2) {
  return function(...args) {
    const target = this["__v_raw"];
    const rawTarget = toRaw(target);
    const targetIsMap = isMap(rawTarget);
    const isPair = method === "entries" || method === Symbol.iterator && targetIsMap;
    const isKeyOnly = method === "keys" && targetIsMap;
    const innerIterator = target[method](...args);
    const wrap = isShallow2 ? toShallow : isReadonly2 ? toReadonly : toReactive;
    !isReadonly2 && track(
      rawTarget,
      "iterate",
      isKeyOnly ? MAP_KEY_ITERATE_KEY : ITERATE_KEY
    );
    return {
      // iterator protocol
      next() {
        const { value, done } = innerIterator.next();
        return done ? { value, done } : {
          value: isPair ? [wrap(value[0]), wrap(value[1])] : wrap(value),
          done
        };
      },
      // iterable protocol
      [Symbol.iterator]() {
        return this;
      }
    };
  };
}
function createReadonlyMethod(type) {
  return function(...args) {
    if (!!(process.env.NODE_ENV !== "production")) {
      const key = args[0] ? `on key "${args[0]}" ` : ``;
      warn(
        `${capitalize(type)} operation ${key}failed: target is readonly.`,
        toRaw(this)
      );
    }
    return type === "delete" ? false : type === "clear" ? void 0 : this;
  };
}
function createInstrumentations() {
  const mutableInstrumentations2 = {
    get(key) {
      return get(this, key);
    },
    get size() {
      return size(this);
    },
    has,
    add,
    set,
    delete: deleteEntry,
    clear,
    forEach: createForEach(false, false)
  };
  const shallowInstrumentations2 = {
    get(key) {
      return get(this, key, false, true);
    },
    get size() {
      return size(this);
    },
    has,
    add(value) {
      return add.call(this, value, true);
    },
    set(key, value) {
      return set.call(this, key, value, true);
    },
    delete: deleteEntry,
    clear,
    forEach: createForEach(false, true)
  };
  const readonlyInstrumentations2 = {
    get(key) {
      return get(this, key, true);
    },
    get size() {
      return size(this, true);
    },
    has(key) {
      return has.call(this, key, true);
    },
    add: createReadonlyMethod("add"),
    set: createReadonlyMethod("set"),
    delete: createReadonlyMethod("delete"),
    clear: createReadonlyMethod("clear"),
    forEach: createForEach(true, false)
  };
  const shallowReadonlyInstrumentations2 = {
    get(key) {
      return get(this, key, true, true);
    },
    get size() {
      return size(this, true);
    },
    has(key) {
      return has.call(this, key, true);
    },
    add: createReadonlyMethod("add"),
    set: createReadonlyMethod("set"),
    delete: createReadonlyMethod("delete"),
    clear: createReadonlyMethod("clear"),
    forEach: createForEach(true, true)
  };
  const iteratorMethods = [
    "keys",
    "values",
    "entries",
    Symbol.iterator
  ];
  iteratorMethods.forEach((method) => {
    mutableInstrumentations2[method] = createIterableMethod(method, false, false);
    readonlyInstrumentations2[method] = createIterableMethod(method, true, false);
    shallowInstrumentations2[method] = createIterableMethod(method, false, true);
    shallowReadonlyInstrumentations2[method] = createIterableMethod(
      method,
      true,
      true
    );
  });
  return [
    mutableInstrumentations2,
    readonlyInstrumentations2,
    shallowInstrumentations2,
    shallowReadonlyInstrumentations2
  ];
}
const [
  mutableInstrumentations,
  readonlyInstrumentations,
  shallowInstrumentations,
  shallowReadonlyInstrumentations
] = /* @__PURE__ */ createInstrumentations();
function createInstrumentationGetter(isReadonly2, shallow) {
  const instrumentations = shallow ? isReadonly2 ? shallowReadonlyInstrumentations : shallowInstrumentations : isReadonly2 ? readonlyInstrumentations : mutableInstrumentations;
  return (target, key, receiver) => {
    if (key === "__v_isReactive") {
      return !isReadonly2;
    } else if (key === "__v_isReadonly") {
      return isReadonly2;
    } else if (key === "__v_raw") {
      return target;
    }
    return Reflect.get(
      hasOwn(instrumentations, key) && key in target ? instrumentations : target,
      key,
      receiver
    );
  };
}
const mutableCollectionHandlers = {
  get: /* @__PURE__ */ createInstrumentationGetter(false, false)
};
const readonlyCollectionHandlers = {
  get: /* @__PURE__ */ createInstrumentationGetter(true, false)
};
function checkIdentityKeys(target, has2, key) {
  const rawKey = toRaw(key);
  if (rawKey !== key && has2.call(target, rawKey)) {
    const type = toRawType(target);
    warn(
      `Reactive ${type} contains both the raw and reactive versions of the same object${type === `Map` ? ` as keys` : ``}, which can lead to inconsistencies. Avoid differentiating between the raw and reactive versions of an object and only use the reactive version if possible.`
    );
  }
}

const reactiveMap = /* @__PURE__ */ new WeakMap();
const shallowReactiveMap = /* @__PURE__ */ new WeakMap();
const readonlyMap = /* @__PURE__ */ new WeakMap();
const shallowReadonlyMap = /* @__PURE__ */ new WeakMap();
function targetTypeMap(rawType) {
  switch (rawType) {
    case "Object":
    case "Array":
      return 1 /* COMMON */;
    case "Map":
    case "Set":
    case "WeakMap":
    case "WeakSet":
      return 2 /* COLLECTION */;
    default:
      return 0 /* INVALID */;
  }
}
function getTargetType(value) {
  return value["__v_skip"] || !Object.isExtensible(value) ? 0 /* INVALID */ : targetTypeMap(toRawType(value));
}
function reactive(target) {
  if (isReadonly(target)) {
    return target;
  }
  return createReactiveObject(
    target,
    false,
    mutableHandlers,
    mutableCollectionHandlers,
    reactiveMap
  );
}
function readonly(target) {
  return createReactiveObject(
    target,
    true,
    readonlyHandlers,
    readonlyCollectionHandlers,
    readonlyMap
  );
}
function createReactiveObject(target, isReadonly2, baseHandlers, collectionHandlers, proxyMap) {
  if (!isObject(target)) {
    if (!!(process.env.NODE_ENV !== "production")) {
      warn(
        `value cannot be made ${isReadonly2 ? "readonly" : "reactive"}: ${String(
          target
        )}`
      );
    }
    return target;
  }
  if (target["__v_raw"] && !(isReadonly2 && target["__v_isReactive"])) {
    return target;
  }
  const existingProxy = proxyMap.get(target);
  if (existingProxy) {
    return existingProxy;
  }
  const targetType = getTargetType(target);
  if (targetType === 0 /* INVALID */) {
    return target;
  }
  const proxy = new Proxy(
    target,
    targetType === 2 /* COLLECTION */ ? collectionHandlers : baseHandlers
  );
  proxyMap.set(target, proxy);
  return proxy;
}
function isReadonly(value) {
  return !!(value && value["__v_isReadonly"]);
}
function isShallow(value) {
  return !!(value && value["__v_isShallow"]);
}
function toRaw(observed) {
  const raw = observed && observed["__v_raw"];
  return raw ? toRaw(raw) : observed;
}
const toReactive = (value) => isObject(value) ? reactive(value) : value;
const toReadonly = (value) => isObject(value) ? readonly(value) : value;

const COMPUTED_SIDE_EFFECT_WARN = `Computed is still dirty after getter evaluation, likely because a computed is mutating its own dependency in its getter. State mutations in computed getters should be avoided.  Check the docs for more details: https://vuejs.org/guide/essentials/computed.html#getters-should-be-side-effect-free`;
class ComputedRefImpl {
  constructor(getter, _setter, isReadonly, isSSR) {
    this.getter = getter;
    this._setter = _setter;
    this.dep = void 0;
    this.__v_isRef = true;
    this["__v_isReadonly"] = false;
    this.effect = new ReactiveEffect(
      () => getter(this._value),
      () => triggerRefValue(
        this,
        this.effect._dirtyLevel === 2 ? 2 : 3
      )
    );
    this.effect.computed = this;
    this.effect.active = this._cacheable = !isSSR;
    this["__v_isReadonly"] = isReadonly;
  }
  get value() {
    const self = toRaw(this);
    if ((!self._cacheable || self.effect.dirty) && hasChanged(self._value, self._value = self.effect.run())) {
      triggerRefValue(self, 4);
    }
    trackRefValue(self);
    if (self.effect._dirtyLevel >= 2) {
      if (!!(process.env.NODE_ENV !== "production") && this._warnRecursive) {
        warn(COMPUTED_SIDE_EFFECT_WARN, `

getter: `, this.getter);
      }
      triggerRefValue(self, 2);
    }
    return self._value;
  }
  set value(newValue) {
    this._setter(newValue);
  }
  // #region polyfill _dirty for backward compatibility third party code for Vue <= 3.3.x
  get _dirty() {
    return this.effect.dirty;
  }
  set _dirty(v) {
    this.effect.dirty = v;
  }
  // #endregion
}

function trackRefValue(ref2) {
  var _a;
  if (shouldTrack && activeEffect) {
    ref2 = toRaw(ref2);
    trackEffect(
      activeEffect,
      (_a = ref2.dep) != null ? _a : ref2.dep = createDep(
        () => ref2.dep = void 0,
        ref2 instanceof ComputedRefImpl ? ref2 : void 0
      ),
      !!(process.env.NODE_ENV !== "production") ? {
        target: ref2,
        type: "get",
        key: "value"
      } : void 0
    );
  }
}
function triggerRefValue(ref2, dirtyLevel = 4, newVal, oldVal) {
  ref2 = toRaw(ref2);
  const dep = ref2.dep;
  if (dep) {
    triggerEffects(
      dep,
      dirtyLevel,
      !!(process.env.NODE_ENV !== "production") ? {
        target: ref2,
        type: "set",
        key: "value",
        newValue: newVal,
        oldValue: oldVal
      } : void 0
    );
  }
}
function isRef(r) {
  return !!(r && r.__v_isRef === true);
}
function ref(value) {
  return createRef(value, false);
}
function createRef(rawValue, shallow) {
  if (isRef(rawValue)) {
    return rawValue;
  }
  return new RefImpl(rawValue, shallow);
}
class RefImpl {
  constructor(value, __v_isShallow) {
    this.__v_isShallow = __v_isShallow;
    this.dep = void 0;
    this.__v_isRef = true;
    this._rawValue = __v_isShallow ? value : toRaw(value);
    this._value = __v_isShallow ? value : toReactive(value);
  }
  get value() {
    trackRefValue(this);
    return this._value;
  }
  set value(newVal) {
    const useDirectValue = this.__v_isShallow || isShallow(newVal) || isReadonly(newVal);
    newVal = useDirectValue ? newVal : toRaw(newVal);
    if (hasChanged(newVal, this._rawValue)) {
      const oldVal = this._rawValue;
      this._rawValue = newVal;
      this._value = useDirectValue ? newVal : toReactive(newVal);
      triggerRefValue(this, 4, newVal, oldVal);
    }
  }
}
class ObjectRefImpl {
  constructor(_object, _key, _defaultValue) {
    this._object = _object;
    this._key = _key;
    this._defaultValue = _defaultValue;
    this.__v_isRef = true;
  }
  get value() {
    const val = this._object[this._key];
    return val === void 0 ? this._defaultValue : val;
  }
  set value(newVal) {
    this._object[this._key] = newVal;
  }
  get dep() {
    return getDepFromReactive(toRaw(this._object), this._key);
  }
}
class GetterRefImpl {
  constructor(_getter) {
    this._getter = _getter;
    this.__v_isRef = true;
    this.__v_isReadonly = true;
  }
  get value() {
    return this._getter();
  }
}
function toRef(source, key, defaultValue) {
  if (isRef(source)) {
    return source;
  } else if (isFunction(source)) {
    return new GetterRefImpl(source);
  } else if (isObject(source) && arguments.length > 1) {
    return propertyToRef(source, key, defaultValue);
  } else {
    return ref(source);
  }
}
function propertyToRef(source, key, defaultValue) {
  const val = source[key];
  return isRef(val) ? val : new ObjectRefImpl(source, key, defaultValue);
}

function useLogger(isDevMode) {
  const devMode = toRef(isDevMode);
  function log(value, ...extras) {
    if (devMode.value)
      return console.log(value, extras);
  }
  function logTable(value) {
    if (devMode.value)
      return console.table(value);
  }
  function warn(value, ...extras) {
    if (devMode.value)
      return console.warn(value, extras);
  }
  function logError(value, ...extras) {
    if (devMode.value)
      return console.error(value, extras);
  }
  function logInfo(value, ...extras) {
    if (devMode.value)
      return console.info(value, extras);
  }
  return {
    log,
    warn,
    logTable,
    logError,
    logInfo
  };
}

const statusTextDict = {
  400: "Bad Request",
  401: "Unauthorized",
  402: "Payment Required",
  403: "Forbidden",
  404: "Not Found",
  405: "Method Not Allowed",
  406: "Not Acceptable",
  407: "Proxy Authentication Required",
  408: "Request Timeout",
  409: "Conflict",
  410: "Gone",
  411: "Length Required",
  412: "Precondition Failed",
  413: "Payload Too Large",
  414: "URI Too Long",
  415: "Unsupported Media Type",
  416: "Range Not Satisfiable",
  417: "Expectation Failed",
  418: "I'm a teapot",
  421: "Misdirected Request",
  422: "Unprocessable Entity",
  423: "Locked",
  424: "Failed Dependency",
  426: "Upgrade Required",
  428: "Precondition Required",
  429: "Too Many Requests",
  431: "Request Header Fields Too Large",
  451: "Unavailable For Legal Reasons",
  500: "Internal Server Error",
  501: "Not Implemented",
  502: "Bad Gateway",
  503: "Service Unavailable",
  504: "Gateway Timeout",
  505: "HTTP Version Not Supported",
  506: "Variant Also Negotiates",
  507: "Insufficient Storage",
  508: "Loop Detected",
  510: "Not Extended",
  511: "Network Authentication Required",
  undefined: "Unknown"
};
const isDevMode = process.env.NODE_ENV === "development";
const isClient = typeof window === "object" && window !== null;
function isChrome(agent) {
  const chromeMatch = agent.match(/(Chrome)\/(\d+(\.\d+)?)/i);
  if (chromeMatch != null) {
    return { name: chromeMatch[1], version: chromeMatch[2] };
  }
}
function isEdge(agent) {
  const edeMatch = agent.match(/(Edg)\/(\d+(\.\d+)?)/i);
  if (edeMatch != null) {
    return { name: "Edge", version: edeMatch[2] };
  }
  return void 0;
}
function isSafari(agent) {
  const safariMatch = agent.match(/(Safari)\/(\d+(\.\d+)?)/i);
  if (safariMatch != null) {
    const version = agent.match(/Version\/(\d+(\.\d+)?)/i);
    return {
      name: safariMatch[1],
      version: version ? version[1] : safariMatch[2]
    };
  }
  return void 0;
}
function isFirefox(agent) {
  const firefoxMatch = agent.match(/(Firefox)\/(\d+(\.\d+)?)/i);
  if (firefoxMatch != null) {
    return { name: firefoxMatch[1], version: firefoxMatch[2] };
  }
  return void 0;
}
function useBrowserAgent(userAgent) {
  if (!userAgent) {
    return { name: "Unknown(Using SSR)", version: "" };
  }
  return isEdge(userAgent) ?? isChrome(userAgent) ?? isFirefox(userAgent) ?? isSafari(userAgent) ?? { name: "Unknown", version: "" };
}
function getOSName() {
  if (!isClient) {
    return "Unknown OS";
  }
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const osList = {
    "Windows": /Win/,
    "Mac OS": /Mac/,
    "Linux": /(Linux|X11)/,
    "iOS": /(iPhone|iPad|iPod)/,
    "Android": /Android/
  };
  for (const os in osList) {
    if (osList[os].test(platform) || osList[os].test(userAgent)) {
      return os;
    }
  }
  return "Unknown OS";
}
function getGeoLocationDetails() {
  return new Promise((resolve) => {
    if (typeof navigator !== "undefined" && navigator.permissions && navigator.geolocation) {
      navigator.permissions.query({
        name: "geolocation"
      }).then(
        (permission) => (
          // is geolocation granted?
          permission.state === "granted" ? navigator.geolocation.getCurrentPosition(
            (position) => {
              const latitude = position.coords.latitude;
              const longitude = position.coords.longitude;
              resolve({ latitude, longitude });
            },
            (error) => {
              console.error("Geolocation error:", error);
              resolve(null);
            }
          ) : resolve(null)
        )
      ).catch((error) => {
        console.error("Permission query error:", error);
        resolve(null);
      });
    } else {
      resolve(null);
    }
  });
}
async function getGeoLocationCoordinates() {
  try {
    const position = await getGeoLocationDetails();
    if (position) {
      return {
        lat: position?.latitude,
        lng: position?.longitude
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error("An error occurred:", error);
    return null;
  }
}
function convertToPascalCase(text) {
  if (!text)
    return void 0;
  return text.replace(/[^a-z0-9]+/gi, " ").replace(/\b\w/g, (match) => match.toUpperCase()).replace(/\s+/g, "");
}
function getValueType(value) {
  if (typeof value === "number") {
    if (Number.isInteger(value)) {
      return "Int";
    } else {
      return "Decimal";
    }
  } else if (typeof value === "string") {
    return "String";
  } else if (typeof value === "object" && value !== null) {
    return "Object";
  } else {
    return "Object";
  }
}
function hyphenateText(str) {
  return str.replace(/\s+/g, "-").toLowerCase();
}
function getNuxtEnvVariable(key) {
  if (typeof import.meta?.env !== "undefined") {
    return import.meta.env[key] || import.meta.env[`VITE_${key}`] || import.meta.env[`NUXT_PUBLIC_${key}`];
  }
  return void 0;
}
function getProductVersionEnvVariable() {
  return getNuxtEnvVariable("APP_VERSION") || process.env.NEXT_PUBLIC_APP_VERSION;
}

function useClientError(err) {
  const { logError } = useLogger(isDevMode);
  const derivedMessage = err?.message ?? err?.data?.message ?? err?._data?.message ?? "Some error happened: Had no description or message";
  logError(`NETERR::${derivedMessage}`);
  logError(`ERRSTACK::${err?.stack}`);
}

const name = "@hubtel/event-publisher";
const type = "module";
const version = "0.4.10";
const description = "Publish events to the Hubtel Events Analytics store";
const repository = {
	type: "git",
	url: "https://hubtel@dev.azure.com/hubtel/Front-End/_git/Hubtel-Events-Publisher"
};
const exports = {
	".": {
		types: "./dist/index.d.ts",
		"import": "./dist/index.js",
		require: "./dist/index.cjs"
	}
};
const main = "./dist/index.js";
const module = "./dist/index.cjs";
const types = "./dist/index.d.ts";
const files = [
	"dist"
];
const scripts = {
	build: " unbuild",
	prepack: "npm run lintfix && npm run build",
	types: "vue-tsc",
	lint: "eslint  .",
	lintfix: "eslint --fix",
	test: "npm test:unit && npm test:types",
	"test:unit": "vitest",
	"test:types": "tsc --noEmit"
};
const peerDependencies = {
	ohash: "^1.1.3"
};
const dependencies = {
	"@datadog/browser-logs": "^5.34.1",
	"@datadog/browser-rum": "^5.34.1"
};
const devDependencies = {
	"@antfu/eslint-config": "^2.21.2",
	"@types/node": "^20.14.9",
	"@vue/reactivity": "^3.4.31",
	eslint: "^9.6.0",
	"happy-dom": "^14.12.3",
	typescript: "5.3.3",
	unbuild: "^2.0.0",
	vitest: "^1.6.0"
};
const pkg = {
	name: name,
	type: type,
	version: version,
	"private": false,
	description: description,
	repository: repository,
	exports: exports,
	main: main,
	module: module,
	types: types,
	files: files,
	scripts: scripts,
	peerDependencies: peerDependencies,
	dependencies: dependencies,
	devDependencies: devDependencies
};

const ANALYTICS_BASE_URL = "https://analytics-api.hubtel.com/api/v1/events";
const pkgProperties = {
  name: pkg.name,
  version: pkg.version
};

async function createAnalyticsPayload(data, presetConfig) {
  const location = isClient ? window.location : null;
  const document = isClient ? window.document : null;
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : null;
  const os = getOSName();
  const customerLocation = await getGeoLocationCoordinates();
  const payload = {
    Page: {
      appId: presetConfig.appId,
      appVersion: presetConfig.appVersion ?? "1.0.0",
      appName: presetConfig.appName ?? location?.origin,
      viewName: data?.viewName ?? location?.href,
      DeviceType: os,
      browser: useBrowserAgent(userAgent).name,
      createdAt: (/* @__PURE__ */ new Date()).toISOString(),
      sectionName: data.sectionName ?? presetConfig.sectionName,
      appBuildNumber: presetConfig.appBuildNumber ?? null,
      tapName: data.tapName ?? null,
      traceId: data.traceId ?? null,
      tapId: data.tapId ?? null,
      uiType: data.uiType,
      pageName: data.pageName ?? location?.href,
      tapShortName: data.actionName ?? null,
      viewId: data.viewId ?? location?.href,
      integrationAppName: data.integrationAppName ?? null,
      viewShortName: data.viewShortName ?? document?.title,
      searchId: data.searchId,
      searchResultFound: data.searchResultFound ?? false,
      searchQuery: data.searchQuery ?? location?.search,
      searchShortName: data.searchShortName,
      searchName: data.searchName,
      searchSelectedResult: data.searchSelectedResult,
      purchasePaymentType: data.purchasePaymentType,
      purchaseErrorMessage: data.purchaseErrorMessage,
      purchasePaymentChannel: data.purchasePaymentChannel,
      purchaseAmount: data.purchaseAmount ?? 0
    },
    Customer: {
      customerPhoneNumber: data.customerPhoneNumber,
      customerName: data.customerName,
      location: customerLocation,
      customerEmail: data.customerEmail,
      customerId: data.customerId,
      customerCountry: data.customerCountry
    },
    Action: {
      eventType: data.eventType,
      actionName: data.actionName,
      isSuccessful: data.eventType === "Error" || data.eventType === "Exception" ? false : data.isSuccessful ?? true,
      apiBaseUrl: data.apiBaseUrl,
      payload: data.payload,
      apiResponse: data.apiResponse,
      startTime: data.startTime,
      endTime: data.endTime,
      apiResponseTimeInSeconds: data.apiResponseTimeInSeconds ?? 0,
      apiResponseSizeInMegabytes: data.apiResponseSizeInKilobytes ? data.apiResponseSizeInKilobytes / 1024 : 0,
      apiStatus: data.apiStatus,
      apiUrl: data.apiUrl,
      apiMethod: data.apiMethod,
      apiRequest: data.apiRequest,
      apiStatusCode: data.apiStatusCode ?? 0,
      sessionDuration: data.sessionDuration,
      ReasonForfailure: data.ReasonForfailure,
      ValidationErrors: data.ValidationErrors,
      apiResponseSizeInKilobytes: data.apiResponseSizeInKilobytes ?? 0
    },
    additionalProperties: {
      ...data.additionalProperties
    }
  };
  return payload;
}
async function sendToAnalytics(data, options) {
  if (options.developerMode) {
    console.info("Posting Product Analytics Event: ", data);
    return;
  }
  fetch(ANALYTICS_BASE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${options.eventAnalyticsApiToken}`
    },
    body: JSON.stringify(data)
  }).then((response) => {
    if (response.ok) ;
  }).catch((error) => {
    console.error("Error posting product analytics event:", error);
  });
}
function prepareEventPublisherPayload(data) {
  const sharedPamaraters = {
    sectionName: convertToPascalCase(data?.Page.sectionName),
    // subSectionName: "",
    userId: data?.Customer.customerId,
    userEmail: data?.Customer?.customerEmail,
    userName: data?.Customer?.customerName,
    userLocationLat: data?.Customer?.location?.lat,
    userLocationLong: data?.Customer?.location?.lng,
    // userLocationName: "",
    // userGender: "",
    userCountry: data?.Customer?.customerCountry
  };
  const event = {
    ...sharedPamaraters,
    eventType: data.Action.eventType,
    eventId: convertToPascalCase(data.Action.actionName),
    eventDescription: data.Action.actionName
  };
  switch (data.Action.eventType) {
    case "Regular":
      event.eventValue = {
        type: getValueType(data.Action?.payload),
        value: data.Action?.payload ?? null
      };
      break;
    case "Error":
      event.method = data.Action?.apiMethod ?? "";
      event.url = data.Action?.apiUrl;
      event.status = data.Action?.apiStatusCode ?? 0;
      event.errorMessage = data.Action?.ReasonForfailure;
      event.request = data.Action?.apiRequest;
      event.response = data.Action?.apiResponse;
      break;
    case "Exception":
      event.errorMessage = data.Action?.ReasonForfailure;
      break;
    case "Duration":
      event.eventId = `${event.eventId}Duration`;
      event.startTime = data.Action?.startTime;
      event.endTime = data.Action?.endTime;
      break;
  }
  return event;
}

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const _HubtelEventPub = class _HubtelEventPub {
  constructor(_config) {
    __publicField(this, "config");
    __publicField(this, "queue", []);
    __publicField(this, "history", {});
    __publicField(this, "isPosting", false);
    __publicField(this, "baseURL", "https://events-analytics.hubtel.com/api/hubtel-events");
    __publicField(this, "logger");
    __publicField(this, "isDatadogRUMEnabled", false);
    __publicField(this, "isDatadogLoggingEnabled", false);
    __publicField(this, "datadogLogs", datadogLogs);
    __publicField(this, "datadogRum", datadogRum);
    __publicField(this, "checkPreset", () => {
      const missingKeys = [];
      const requiredKeys = [
        "appBuildNumber",
        "appId",
        "appName",
        "appVersion"
      ];
      Object.keys(this.config).forEach((k) => {
        if (requiredKeys.includes(k) && (this.config[k] === void 0 || !this.config[k])) {
          missingKeys.push(k);
        }
      });
      if (missingKeys.length) {
        const constructorErr = new Error(
          `Provide values for ${missingKeys.join(", ")} preset values`
        );
        useClientError(constructorErr);
      }
    });
    if (_HubtelEventPub.instance) {
      return _HubtelEventPub.instance;
    }
    this.config = {
      ...{
        lazy: true,
        developerMode: process.env.NODE_ENV === "development",
        appPlatform: "Web",
        sendToEventAnalytics: true,
        sendToEventsPortal: true
      },
      ..._config,
      appBuildNumber: _config.appVersion
    };
    this.logger = useLogger(this.config?.developerMode);
    this.checkPreset();
    this.logRegularEvent = this.logRegularEvent.bind(this);
    this.logDurationEvent = this.logDurationEvent.bind(this);
    this.logRequestErrorEvent = this.logRequestErrorEvent.bind(this);
    this.logExceptionEvent = this.logExceptionEvent.bind(this);
    this.handlePrepareEvent = this.handlePrepareEvent.bind(this);
    this.logAnalyticsEvent = this.logAnalyticsEvent.bind(this);
    this.initDatadogRUM = this.initDatadogRUM.bind(this);
    _HubtelEventPub.instance = this;
  }
  setEventValue(eventValue, event) {
    const { type, value } = eventValue;
    const eventTypeMap = {
      double: () => event.eventValueDouble = value,
      decimal: () => event.eventValueDecimal = value,
      int: () => event.eventValueInt = value,
      string: () => event.eventValueString = value,
      object: () => event.eventValueObject = value
    };
    return eventTypeMap[type.toLowerCase()]();
  }
  hashEventPayload({ eventDescription, eventId }, value) {
    return hash({ eventDescription, eventId, ...value });
  }
  handlePrepareEvent(eventData, eventValue) {
    const eventPayload = { ...eventData };
    this.setEventValue(eventValue, eventPayload);
    const userAgent = this.config?.isServerApp ? this.config.userAgent : isClient ? navigator.userAgent : null;
    const client = useBrowserAgent(userAgent);
    eventPayload.appOs = `${getOSName()} - ${client?.name} ${client?.version}`;
    eventPayload.eventId = eventData.eventId?.replaceAll(" ", "");
    const hashedEvent = this.hashEventPayload(eventData, eventValue);
    this.updateHistory(hashedEvent, {
      ...eventPayload,
      ...this.config,
      sectionName: eventData.sectionName ?? this.config.sectionName
    });
  }
  purgeQueue(firedRequests) {
    this.queue.forEach((_, index) => {
      firedRequests[index]?.then((res) => {
        if (res.ok)
          return this.queue.splice(index, 1);
      }).catch(
        (error) => useLogger(this.config.isServerApp).log(error, "Failed To Post")
      );
    });
  }
  updateHistory(hashedEvent, event) {
    if (this.history[hashedEvent] && !this.config.developerMode) {
      return;
    }
    const isErrorOrException = ["Error", "Exception"].includes(
      event.eventType
    );
    if (!isErrorOrException) {
      this.queue.push(event);
      this.logger.logInfo(`Posting ${event.eventType} Event: `, event);
    } else if (this.history[hashedEvent]) {
      this.history[hashedEvent] += 1;
    } else {
      this.history[hashedEvent] = 1;
      this.queue.push(event);
    }
    this.triggerEventQueue();
    if (this.config.developerMode && isErrorOrException) {
      this.logger.warn(
        event,
        `Has occurred ${this.history[hashedEvent]} time(s)`
      );
      this.logger.warn(
        "You're seeing this because this is a dev environment. In production, error and exception events will be logged once"
      );
    }
  }
  triggerEventQueue() {
    if (!this.queue?.length || this.isPosting) {
      return;
    }
    this.isPosting = true;
    this.startQueue();
  }
  startQueue() {
    const loggerResults = {};
    this.queue?.forEach((event, i) => {
      if (this.config?.developerMode) {
        loggerResults[i] = Promise.resolve({ ok: true });
        return;
      }
      const promise = fetch(this.baseURL, {
        method: "POST",
        body: JSON.stringify(event),
        headers: {
          "content-type": "application/json"
        }
        // mode: 'cors',
      });
      loggerResults[i] = promise;
    });
    this.isPosting = false;
    this.purgeQueue(loggerResults);
  }
  logDurationEvent(payload) {
    try {
      const { startTime, endTime, ...eventBase } = payload;
      const start = new Date(startTime);
      const end = new Date(endTime);
      const timeSpent = end.getTime() - start.getTime();
      payload.eventType = "Duration";
      const eventValue = { type: "Double", value: timeSpent };
      this.handlePrepareEvent(eventBase, eventValue);
    } catch (error) {
      useClientError(error);
    }
  }
  /** Log custom, non-alert events to keep track of user activity */
  logRegularEvent(payload) {
    try {
      payload.eventType = "Regular";
      const { eventValue, ...eventBase } = payload;
      this.handlePrepareEvent(eventBase, eventValue);
    } catch (error) {
      useClientError(error);
    }
  }
  /** Log request and fetch errors */
  logRequestErrorEvent(payload) {
    try {
      payload.eventType = "Error";
      const {
        status,
        url,
        method,
        request,
        response,
        errorMessage,
        ...eventBase
      } = payload;
      const eventValue = {
        url: `${method}: ${url}`,
        stack: errorMessage,
        status,
        request,
        response
      };
      if (!eventBase?.eventId) {
        const eventIdWithCode = statusTextDict[status];
        eventBase.eventId = eventIdWithCode;
      }
      if (!eventBase.eventDescription) {
        eventBase.eventDescription = statusTextDict[status];
      }
      this.handlePrepareEvent(eventBase, {
        type: "String",
        value: JSON.stringify(eventValue, null, 3)
      });
    } catch (error) {
      useClientError(error);
    }
  }
  /** Log app breaking exceptions and errors */
  logExceptionEvent(payload) {
    try {
      payload.eventType = "Exception";
      const { errorMessage, ...eventBase } = payload;
      if (!eventBase?.eventId) {
        eventBase.eventId = "ExceptionEvent";
      }
      this.handlePrepareEvent(eventBase, {
        type: "String",
        value: errorMessage
      });
    } catch (error) {
      useClientError(error);
    }
  }
  initDatadogRUM() {
    const rumConfig = this.config?.rumConfig;
    if (this.config.developerMode) {
      this.logger.warn("Datadog RUM is disabled in Developer Mode");
      return;
    }
    try {
      if (process.env.NODE_ENV === "production" || !this.config.developerMode) {
        this.datadogRum.init({
          env: process.env.NODE_ENV === "development" ? "development" : "production",
          site: "datadoghq.com",
          sessionSampleRate: 100,
          sessionReplaySampleRate: 70,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          ...rumConfig,
          service: this.config?.rumConfig?.service ?? hyphenateText(this.config.appName),
          clientToken: this.config?.clientToken || rumConfig?.clientToken || "",
          applicationId: this.config?.rumConfig?.applicationId || this.config?.appId || "",
          version: this.config.appVersion,
          defaultPrivacyLevel: "mask-user-input"
        });
        this.isDatadogRUMEnabled = true;
      }
    } catch (err) {
      this.logger.logError(err);
    }
  }
  initDatadogLogging() {
    const logsConfig = this.config?.logsConfig;
    if (this.config.developerMode) {
      this.logger.warn("Datadog Logging is disabled in Developer Mode");
      return;
    }
    try {
      this.datadogLogs.init({
        site: "datadoghq.com",
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        env: process.env.NODE_ENV === "development" ? "development" : "production",
        ...logsConfig,
        service: this.config?.rumConfig?.service ?? hyphenateText(this.config.appName),
        clientToken: this.config?.clientToken || this.config?.rumConfig?.clientToken || ""
      });
      this.isDatadogLoggingEnabled = true;
    } catch (err) {
      this.logger.logError(err);
    }
  }
  initObservability() {
    this.initDatadogRUM();
    this.initDatadogLogging();
    let tags = this.config.tags || "{}";
    if (typeof tags === "object") {
      tags = JSON.stringify(tags);
    } else if (typeof tags === "string") {
      try {
        JSON.parse(tags);
      } catch {
        tags = "{}";
      }
    } else {
      tags = "{}";
    }
    const context = {
      "host": window.location.host,
      "package": pkgProperties,
      ...JSON.parse(tags),
      "product.hsvn": this.getProductVersion()
    };
    this.datadogLogs.setGlobalContext(context);
    this.datadogRum.setGlobalContext(context);
  }
  info(message, messageContext, error) {
    this.datadogLogs.logger.info(message, messageContext, error);
  }
  error(message, messageContext, error) {
    this.datadogLogs.logger.error(message, messageContext, error);
  }
  warn(message, messageContext, error) {
    this.datadogLogs.logger.warn(message, messageContext, error);
  }
  debug(message, messageContext, error) {
    this.datadogLogs.logger.debug(message, messageContext, error);
  }
  setGlobalContextProperty(key, context) {
    this.datadogRum.setGlobalContextProperty(key, { ...context, host: window.location.host });
    this.datadogLogs.setGlobalContextProperty(key, { ...context, host: window.location.host });
  }
  setUser(newUser) {
    this.datadogRum.setUser(newUser);
    this.datadogLogs.setUser(newUser);
  }
  addAction(name, context) {
    this.datadogRum.addAction(name, context);
  }
  trackUserAction(action, context) {
    this.datadogRum.addAction(action, context);
  }
  async logAnalyticsEvent(payload, config = { sendToEventsPortal: true }) {
    const event = await createAnalyticsPayload(payload, this.config);
    if (this.isDatadogLoggingEnabled && this.isDatadogRUMEnabled) {
      this.datadogLogs.logger.info(event.Action.actionName, { ProductEvent: event });
    }
    if (this.config.sendToEventAnalytics) {
      await sendToAnalytics(event, {
        developerMode: this.config.developerMode,
        eventAnalyticsApiToken: this.config.eventAnalyticsApiToken
      });
    }
    if (!this.config.sendToEventsPortal || !config.sendToEventsPortal) {
      return;
    }
    const data = prepareEventPublisherPayload(event);
    switch (data.eventType) {
      case "Duration":
        this.logDurationEvent(data);
        break;
      case "Regular":
        this.logRegularEvent(data);
        break;
      case "Error":
        this.logRequestErrorEvent(data);
        break;
      case "Exception":
        this.logExceptionEvent(data);
        break;
    }
  }
  getProductVersion() {
    return getProductVersionEnvVariable();
  }
};
__publicField(_HubtelEventPub, "instance");
let HubtelEventPub = _HubtelEventPub;

const baseURL = "https://events-analytics.hubtel.com/api/hubtel-events";
const _config = {
  developerMode: process.env.NODE_ENV === "development"
};
const isPosting = ref(false);
const eventQueue = reactive([]);
const eventHistory = {};
let devLogger;
function HubtelEventPubV2(userConfig) {
  Object.assign(_config, { ..._config, ...userConfig });
  devLogger = useLogger(_config?.developerMode);
  const logDurationEvent = (payload) => fireDurationEvent(payload);
  const logRegularEvent = (payload) => fireRegularEvent(payload);
  const logRequestErrorEvent = (payload) => fireRequestErrorEvent(payload);
  const logExceptionEvent = (payload) => fireExceptionEvent(payload);
  return {
    logDurationEvent,
    logRegularEvent,
    logRequestErrorEvent,
    logExceptionEvent
  };
}
function setEventValue(eventValue, target) {
  const { type, value } = eventValue;
  const eventTypeMap = {
    double: () => target.eventValueDouble = value,
    decimal: () => target.eventValueDecimal = value,
    int: () => target.eventValueInt = value,
    string: () => target.eventValueString = value,
    object: () => target.eventValueObject = value
  };
  return eventTypeMap[type.toLowerCase()]();
}
function hashEventPayload({ eventDescription, eventId }, value) {
  return hash({ eventDescription, eventId, ...value });
}
async function startQueue() {
  const loggerResults = {};
  eventQueue?.forEach((event, i) => {
    if (_config?.developerMode) {
      loggerResults[i] = Promise.resolve({ ok: true });
      return;
    }
    const promise = fetch(baseURL, {
      method: "POST",
      body: JSON.stringify(event),
      headers: {
        "content-type": "application/json"
      },
      mode: "cors"
    });
    loggerResults[i] = promise;
  });
  isPosting.value = false;
  purgeEventQueue(loggerResults);
}
function purgeEventQueue(firedRequests) {
  eventQueue.forEach((_, index) => {
    firedRequests[index]?.then((res) => {
      if (res.ok)
        return eventQueue.splice(index, 1);
    }).catch((error) => useLogger(_config?.isServerApp).log(error, "Failed To Post"));
  });
}
function triggerQueue() {
  if (!eventQueue?.length || isPosting.value) {
    return;
  }
  isPosting.value = true;
  startQueue();
}
function updateHistory(hashedEvent, event) {
  if (eventHistory[hashedEvent] && !_config?.developerMode) {
    return;
  }
  const isErrorOrException = ["Error", "Exception"].includes(
    event.eventType
  );
  if (!isErrorOrException) {
    eventQueue.push(event);
    devLogger.logInfo(`Posting ${event.eventType} Event: `, event);
  } else if (eventHistory[hashedEvent]) {
    eventHistory[hashedEvent] += 1;
  } else {
    eventHistory[hashedEvent] = 1;
    eventQueue.push(event);
  }
  triggerQueue();
  if (_config?.developerMode && isErrorOrException) {
    devLogger.warn(
      event,
      `Has occurred ${eventHistory[hashedEvent]} time(s)`
    );
    devLogger.warn(
      "You're seeing this because this is a dev environment. In production, error and exception events will be logged once"
    );
  }
}
function prepareEvent(eventData, eventValue) {
  if (!_config)
    throw new Error("Provide the required configuration");
  const eventPayload = { ...eventData };
  setEventValue(eventValue, eventPayload);
  const userAgent = _config?.isServerApp ? _config.userAgent : navigator?.userAgent;
  const client = useBrowserAgent(userAgent);
  eventPayload.appOs = `${client?.name} ${client?.version}`;
  eventPayload.eventId = eventData.eventId?.replaceAll(" ", "");
  const hashedEvent = hashEventPayload(eventData, eventValue);
  updateHistory(
    hashedEvent,
    { ...eventPayload, ..._config }
  );
}
function fireDurationEvent(payload) {
  try {
    const { startTime, endTime, ...eventBase } = payload;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const timeSpent = end.getTime() - start.getTime();
    eventBase.eventType = "Duration";
    const eventValue = { type: "Double", value: timeSpent };
    prepareEvent(eventBase, eventValue);
  } catch (error) {
    useClientError(error);
  }
}
function fireRegularEvent(payload) {
  try {
    payload.eventType = "Regular";
    const { eventValue, ...eventBase } = payload;
    prepareEvent(eventBase, eventValue);
  } catch (error) {
    useClientError(error);
  }
}
function fireRequestErrorEvent(payload) {
  try {
    payload.eventType = "Error";
    const {
      status,
      url,
      method,
      request,
      response,
      errorMessage,
      ...eventBase
    } = payload;
    const eventValue = {
      url: `${method}: ${url}`,
      stack: errorMessage,
      status,
      request,
      response
    };
    if (!eventBase?.eventId) {
      const eventIdWithCode = statusTextDict[status];
      eventBase.eventId = eventIdWithCode;
    }
    if (!eventBase.eventDescription) {
      eventBase.eventDescription = statusTextDict[status];
    }
    prepareEvent(eventBase, {
      type: "String",
      value: JSON.stringify(eventValue, null, 3)
    });
  } catch (error) {
    useClientError(error);
  }
}
function fireExceptionEvent(payload) {
  try {
    payload.eventType = "Exception";
    const { errorMessage, ...eventBase } = payload;
    if (!eventBase?.eventId) {
      eventBase.eventId = "ExceptionEvent";
    }
    prepareEvent(eventBase, { type: "String", value: errorMessage });
  } catch (error) {
    useClientError(error);
  }
}

export { HubtelEventPub, HubtelEventPubV2 };
